/**
 * Main library screen: sidebar + note list + editor.
 *
 * Protocol v2 orchestration layer. It owns the in-memory library state
 * (`LibraryStateV2`), and sync runs through `runSyncFlow` which is strictly
 * single-flight: a second trigger while a sync is running only queues one
 * trailing pass — the v1 bug where a long pull finished after a fast push and
 * wrote its stale arrays over fresh edits cannot recur.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CloudOff, GitMerge, Trash2 } from 'lucide-react';
import { columns, metrics } from '../design/tokens.js';
import { Sidebar } from '../components/Sidebar.js';
import { NoteList } from '../components/NoteList.js';
import { EditorPane, type EditorDraft, type OrganizeView } from '../components/EditorPane.js';
import { SplitHandle } from '../components/SplitHandle.js';
import { VaultGate } from '../components/VaultGate.js';
import { useIsMobile } from '../lib/ui/useMediaQuery.js';
import { useOrganize } from '../lib/ai/organizeController.js';
import { llmApi, type LLMSettingsView } from '../lib/api/llm.js';
import {
  clampNoteListWidth,
  clampSidebarWidth,
  loadNoteListWidth,
  loadSidebarWidth,
  noteListWidthMaxFor,
  saveNoteListWidth,
  saveSidebarWidth,
  sidebarWidthMaxFor,
} from '../lib/ui/paneLayout.js';
import { StorageSettingsDialog } from '../components/StorageSettingsDialog.js';
import { ConflictDialog, type ResolveChoice } from '../components/ConflictDialog.js';
import { VersionHistoryDialog } from '../components/VersionHistoryDialog.js';
import { settingsApi, type StorageSettingsView } from '../lib/api/settings.js';
import { vaultApi, type VaultAttachmentManifest } from '../lib/api/vault.js';
import { ApiError } from '../lib/api/client.js';
import { storageApi } from '../lib/api/storage.js';
import { useSession } from '../lib/auth/session.js';
import {
  decryptVaultAttachments,
  encryptVaultAttachment,
  parseRelativePath,
  revokeVaultAttachmentURLs,
  type DecryptedVaultAttachment,
} from '../lib/vault/attachments.js';
import { pendingVaultNotes, withDecryptedContent } from '../lib/vault/viewNotes.js';
import { attachmentURL } from '../lib/sync/attachmentCodec.js';
import { attachmentKey } from '../lib/sync/paths.js';
import { sha256Hex } from '../lib/sync/hash.js';
import {
  activeNotes,
  buildFolderTree,
  deleteFolder,
  newId,
  notesInFolder,
  restoreNote,
  sanitizeTitle,
  trashNote,
  trashedNotes,
  vaultNotes,
  type SelectionKind,
} from '../lib/notes/store.js';
import type { FolderRecord, NoteRecord } from '../lib/sync/dto.js';
import {
  markDirty,
  queueDelete,
  resolveConflictV2,
  runSyncV2,
  type LibraryStateV2,
  type SyncConflict,
  type SyncOutcomeV2,
} from '../lib/sync/v2.js';
import { clearLibraryState, loadLibraryState, saveLibraryState } from '../lib/sync/localStore.js';

function nowDate(): Date {
  return new Date();
}

/** Eases a numeric value over a few frames (imported prefs, view changes). */
function tweenValue(
  from: number,
  to: number,
  durationMs: number,
  onUpdate: (value: number) => void,
  onDone?: () => void,
): void {
  if (!Number.isFinite(from) || Math.abs(to - from) < 0.5) {
    onUpdate(to);
    onDone?.();
    return;
  }
  const start = performance.now();
  const step = (now: number): void => {
    const progress = Math.min(1, (now - start) / durationMs);
    const eased = 1 - Math.pow(1 - progress, 3);
    onUpdate(from + (to - from) * eased);
    if (progress < 1) requestAnimationFrame(step);
    else onDone?.();
  };
  requestAnimationFrame(step);
}

/**
 * Restores synchronously on first render so the library is on screen before
 * the first network request returns; the sync then only merges the delta.
 */
const initialState: LibraryStateV2 = loadLibraryState();

export function LibraryScreen() {
  const { logout } = useSession();

  // Mobile layout: list ⇄ editor view stack with the sidebar in a drawer.
  // Below the breakpoint the column widths are irrelevant (and must not be
  // re-clamped to a phone viewport), so the desktop split is only touched
  // while wide.
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Draggable column widths (see lib/ui/paneLayout.ts). Boot values restore
  // the persisted split, clamped to the current window like the Mac.
  const bootLayout = useMemo(() => {
    const viewport = typeof window === 'undefined' ? 1200 : window.innerWidth;
    const sidebar = loadSidebarWidth(viewport);
    return { viewport, sidebar, list: loadNoteListWidth(viewport, sidebar) };
  }, []);
  const [viewportWidth, setViewportWidth] = useState(bootLayout.viewport);
  const [sidebarWidth, setSidebarWidth] = useState(bootLayout.sidebar);
  const [noteListWidth, setNoteListWidth] = useState(bootLayout.list);
  const libraryRef = useRef<HTMLDivElement>(null);
  const widthsRef = useRef({ sidebar: bootLayout.sidebar, list: bootLayout.list });
  widthsRef.current = { sidebar: sidebarWidth, list: noteListWidth };

  // Window resizes re-clamp and persist, mirroring RootView's
  // onChange(of: geo.size.width) + persistSidebarWidth on the Mac. While the
  // mobile layout is active the clamps are suspended — otherwise a phone
  // viewport would squeeze the stored desktop split down to its minimum and
  // persist that. Going (back) to the wide layout re-restores the stored
  // split instead of trusting the in-memory widths.
  useEffect(() => {
    if (isMobile) {
      // The drawer is a mobile-only overlay; never keep it open across a
      // breakpoint jump into the desktop three-pane layout.
      setDrawerOpen(false);
      return;
    }

    const restore = (): void => {
      const viewport = libraryRef.current?.clientWidth ?? window.innerWidth;
      const sidebar = loadSidebarWidth(viewport);
      const list = loadNoteListWidth(viewport, sidebar);
      widthsRef.current = { sidebar, list };
      setViewportWidth(viewport);
      setSidebarWidth(sidebar);
      setNoteListWidth(list);
    };
    restore();

    const onResize = (): void => {
      const viewport = libraryRef.current?.clientWidth ?? window.innerWidth;
      setViewportWidth(viewport);
      const sidebar = clampSidebarWidth(widthsRef.current.sidebar, viewport);
      if (sidebar !== widthsRef.current.sidebar) {
        widthsRef.current.sidebar = sidebar;
        setSidebarWidth(sidebar);
        saveSidebarWidth(sidebar);
      }
      const list = clampNoteListWidth(widthsRef.current.list, viewport, sidebar);
      if (list !== widthsRef.current.list) {
        widthsRef.current.list = list;
        setNoteListWidth(list);
        saveNoteListWidth(list);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isMobile]);

  // Esc dismisses the mobile drawer, mirroring a back gesture.
  useEffect(() => {
    if (!isMobile) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobile]);

  // An imported config backup (Settings → Import from Mac) rewrites the
  // persisted widths; ease to the new split instead of snapping.
  useEffect(() => {
    const onImported = (): void => {
      const viewport = libraryRef.current?.clientWidth ?? window.innerWidth;
      const targetSidebar = loadSidebarWidth(viewport);
      const targetList = loadNoteListWidth(viewport, targetSidebar);
      tweenValue(
        widthsRef.current.sidebar,
        targetSidebar,
        220,
        (value) => {
          widthsRef.current.sidebar = value;
          setSidebarWidth(value);
        },
        () => saveSidebarWidth(targetSidebar),
      );
      tweenValue(
        widthsRef.current.list,
        targetList,
        220,
        (value) => {
          widthsRef.current.list = value;
          setNoteListWidth(value);
        },
        () => saveNoteListWidth(targetList),
      );
    };
    window.addEventListener('jnote:preferences-imported', onImported);
    return () => window.removeEventListener('jnote:preferences-imported', onImported);
  }, []);

  const [folders, setFolders] = useState<FolderRecord[]>(initialState.folders);
  const [notes, setNotes] = useState<NoteRecord[]>(initialState.notes);

  const [selection, setSelection] = useState<{ kind: SelectionKind; folderId: string | null }>({
    kind: 'notes',
    folderId: null,
  });
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const [syncing, setSyncing] = useState(false);
  const [phase, setPhase] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<SyncConflict[]>([]);
  const [showConflicts, setShowConflicts] = useState(false);
  const [historyNoteId, setHistoryNoteId] = useState<string | null>(null);
  const [vaultUnlocked, setVaultUnlocked] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [storageSettings, setStorageSettings] = useState<StorageSettingsView | null>(null);
  /** AI Organize endpoint state; null until fetched (or on failure). */
  const [llmSettings, setLLMSettings] = useState<LLMSettingsView | null>(null);
  /** Decrypted vault note bodies, keyed by note id. Locking clears these. */
  const [decrypted, setDecrypted] = useState<Map<string, { title: string; content: string }>>(
    new Map(),
  );
  /** Decrypted vault attachments: note id -> portable reference -> attachment. */
  const [vaultAttachments, setVaultAttachments] = useState<
    Map<string, Map<string, DecryptedVaultAttachment>>
  >(new Map());

  // The sync engine mutates this object in place; the mirror below feeds React.
  const stateRef = useRef<LibraryStateV2>(initialState);
  stateRef.current.folders = folders;
  stateRef.current.notes = notes;

  /** Single-flight guard + one queued trailing pass (see header comment). */
  const syncingRef = useRef(false);
  const queuedRef = useRef(false);
  const lastSyncAtRef = useRef<number | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);

  /** Persists data + cursor + dirty set atomically (one localStorage key). */
  const persist = useCallback(() => {
    saveLibraryState(stateRef.current, lastSyncAtRef.current);
  }, []);

  const runSyncFlow = useCallback(
    async (options: { pushOnly?: boolean } = {}) => {
      if (syncingRef.current) {
        queuedRef.current = true;
        return;
      }
      syncingRef.current = true;
      setSyncing(true);
      setStatusMessage(null);
      try {
        do {
          queuedRef.current = false;
          let outcome: SyncOutcomeV2;
          try {
            outcome = await runSyncV2(stateRef.current, {
              pushOnly: options.pushOnly,
              onProgress: (stage) => setPhase(stage),
            });
          } catch (error) {
            setStatusMessage(
              error instanceof ApiError ? error.message : `Sync failed: ${(error as Error).message}`,
            );
            return;
          }
          setFolders([...stateRef.current.folders]);
          setNotes([...stateRef.current.notes]);
          setConflicts([...stateRef.current.conflicts.values()]);
          const at = nowDate();
          lastSyncAtRef.current = at.getTime();
          setLastSyncAt(at);
          persist();
          if (outcome.conflicts.length > 0) {
            setStatusMessage(
              outcome.conflicts.length === 1
                ? '1 note has a sync conflict — edits were kept and need resolving.'
                : `${outcome.conflicts.length} notes have sync conflicts — edits were kept and need resolving.`,
            );
          }
        } while (queuedRef.current);
      } finally {
        syncingRef.current = false;
        setSyncing(false);
        setPhase(null);
      }
    },
    [persist],
  );

  useEffect(() => {
    void runSyncFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Local edits persist on a short debounce so a reload never loses them. */
  useEffect(() => {
    const handle = window.setTimeout(() => persist(), 400);
    return () => window.clearTimeout(handle);
  }, [folders, notes, persist]);

  useEffect(() => {
    vaultApi
      .status()
      .then((status) => setVaultUnlocked(status.unlocked))
      .catch(() => setVaultUnlocked(false));
  }, []);

  // Whether the bucket is reachable decides whether we nag the user to
  // configure it, so this is fetched once on mount and refreshed after a save.
  const loadStorageSettings = useCallback(async () => {
    try {
      const result = await settingsApi.getStorage();
      setStorageSettings(result.storage);
    } catch {
      // A failure here must not block the library; the sync error banner
      // already surfaces connectivity problems.
      setStorageSettings(null);
    }
  }, []);

  useEffect(() => {
    void loadStorageSettings();
  }, [loadStorageSettings]);

  // AI Organize availability gates the editor capsule, fetched the same way.
  const loadLLMSettings = useCallback(async () => {
    try {
      setLLMSettings((await llmApi.getSettings()).llm);
    } catch {
      // Not fatal: the capsule stays hidden until a fetch succeeds.
      setLLMSettings(null);
    }
  }, []);

  useEffect(() => {
    void loadLLMSettings();
  }, [loadLLMSettings]);

  const counts = useMemo(
    () => ({
      notes: activeNotes(notes).length,
      trash: trashedNotes(notes).length,
      vault: vaultNotes(notes).length,
    }),
    [notes],
  );

  /**
   * Notes with decrypted content substituted in.
   *
   * A vaulted record's `title`/`content` are intentionally blank on the wire,
   * so the list would show empty rows until each note was opened. Applying the
   * same substitution the editor uses keeps the list and the editor consistent.
   */
  const viewNotes = useMemo<NoteRecord[]>(
    () => withDecryptedContent(notes, decrypted),
    [notes, decrypted],
  );

  const visibleNotes = useMemo(() => {
    if (selection.kind === 'trash') return trashedNotes(viewNotes);
    if (selection.kind === 'vault') return vaultNotes(viewNotes);
    return selection.folderId
      ? notesInFolder(viewNotes, selection.folderId)
      : activeNotes(viewNotes);
  }, [viewNotes, selection]);

  const selectedNote = useMemo(
    () => visibleNotes.find((note) => note.id === selectedNoteId) ?? null,
    [visibleNotes, selectedNoteId],
  );

  const vaultedNoteIds = useMemo(
    () => new Set(notes.filter((note) => note.isVaulted).map((note) => note.id)),
    [notes],
  );

  /**
   * Decrypts one vaulted note into memory, plus its attachment blobs.
   *
   * Vaulted records carry no plaintext: the synced object's title and content
   * are empty and everything readable lives in the ciphertext. So until a note
   * is decrypted it renders blank — including its row in the list.
   */
  const decryptNote = useCallback(async (note: NoteRecord): Promise<void> => {
    if (!note.vaultCiphertextBase64) return;
    const { payload } = await vaultApi.decrypt(note.vaultCiphertextBase64);
    setDecrypted((previous) =>
      new Map(previous).set(note.id, { title: payload.title, content: payload.content }),
    );

    // Attachment plaintext lives only in the encrypted payload; decrypt the
    // blobs into memory so the body's references can render.
    const manifests = (payload.attachments ?? []) as VaultAttachmentManifest[];
    if (manifests.length === 0) return;
    const urls = await decryptVaultAttachments(note.id, manifests);
    setVaultAttachments((previous) => new Map(previous).set(note.id, urls));
  }, []);

  /**
   * Decrypts the selected vaulted note on demand — covers opening a note while
   * the vault is already unlocked.
   */
  useEffect(() => {
    if (!selectedNote || !selectedNote.isVaulted || !vaultUnlocked) return;
    if (decrypted.has(selectedNote.id)) return;
    if (!selectedNote.vaultCiphertextBase64) return;

    let cancelled = false;
    decryptNote(selectedNote).catch((error: unknown) => {
      if (cancelled) return;
      setStatusMessage(
        error instanceof ApiError ? error.message : 'Could not decrypt this note.',
      );
    });

    return () => {
      cancelled = true;
    };
  }, [selectedNote, vaultUnlocked, decrypted, decryptNote]);

  /**
   * Decrypts *every* vaulted note once the vault unlocks.
   *
   * Without this the list stays blank after unlocking: only the selected note
   * was decrypted, and if nothing was selected (or the vault view was showing
   * rows whose titles exist solely inside the ciphertext) nothing appeared
   * until a reload re-ran the flow. Unlocking is the user's signal that they
   * want to read the vault, so fill it all in.
   */
  useEffect(() => {
    if (!vaultUnlocked) return;
    const pending = pendingVaultNotes(notes, decrypted);
    if (pending.length === 0) return;

    let cancelled = false;
    void (async () => {
      for (const note of pending) {
        try {
          await decryptNote(note);
        } catch (error) {
          if (!cancelled) {
            setStatusMessage(
              error instanceof ApiError ? error.message : 'Could not decrypt this note.',
            );
          }
          return;
        }
        if (cancelled) return;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [vaultUnlocked, notes, decrypted, decryptNote]);

  /** Locking discards plaintext and releases every attachment blob URL. */
  useEffect(() => {
    if (vaultUnlocked) return;
    if (vaultAttachments.size === 0 && decrypted.size === 0) return;
    for (const urls of vaultAttachments.values()) revokeVaultAttachmentURLs(urls);
    setVaultAttachments(new Map());
    setDecrypted(new Map());
  }, [vaultUnlocked]);

  const tree = useMemo(() => buildFolderTree(folders), [folders]);

  /** Portable reference -> blob URL for the selected vaulted note. */
  const selectedVaultAttachmentURLs = useMemo(() => {
    const forNote = selectedNote ? vaultAttachments.get(selectedNote.id) : undefined;
    if (!forNote) return undefined;
    return new Map([...forNote].map(([reference, attachment]) => [reference, attachment.url]));
  }, [selectedNote, vaultAttachments]);

  /**
   * The record handed to the editor. `selectedNote` already comes from
   * `viewNotes`, so an unlocked vaulted note carries its decrypted title/body
   * here — the stored record deliberately keeps those empty so plaintext never
   * sits in the synced object.
   */
  const displayedNote = selectedNote;

  // --- AI Organize (Mac `NoteOrganizeController` port) ---
  // The controller subscribes via useSyncExternalStore, so every snapshot
  // change re-renders this screen with fresh capsule state.
  const organize = useOrganize(commitForNote);

  // Leaving the pinned note mid-run stops it and restores, like the Mac's
  // EditorScreen selection-change handler; after completion it retires Undo.
  useEffect(() => {
    organize.noteSwitched(selectedNoteId);
  }, [selectedNoteId, organize]);

  const organizeSnapshot = organize.getSnapshot();
  const organizePinnedHere =
    organizeSnapshot.pinnedNoteId !== null &&
    organizeSnapshot.pinnedNoteId === displayedNote?.id;
  const organizeView: OrganizeView | undefined =
    llmSettings?.configured === true
      ? {
          available: true,
          isStreaming: organizePinnedHere && organizeSnapshot.phase === 'streaming',
          waitingStatus: organizePinnedHere ? organizeSnapshot.waitingStatus : null,
          errorMessage: organizeSnapshot.errorMessage,
          canUndo: organizeSnapshot.canUndo && organizePinnedHere,
          streamedContent:
            organizePinnedHere && organizeSnapshot.phase === 'streaming'
              ? organizeSnapshot.streamedContent
              : null,
          onStart: (draft) => {
            if (displayedNote) organize.start(displayedNote.id, draft);
          },
          onStop: () => organize.stop(),
          onUndo: () => organize.undo(),
          onDismissError: () => organize.dismissError(),
        }
      : undefined;

  function selectKind(kind: SelectionKind): void {
    setDrawerOpen(false);
    // Leaving the Vault view locks the session, exactly like the macOS app's
    // `VaultScreen.onDisappear` — returning always starts at the password gate.
    if (selection.kind === 'vault' && kind !== 'vault') lockVault();
    setSelection({ kind, folderId: null });
    setSelectedNoteId(null);
  }

  function selectFolder(id: string): void {
    setDrawerOpen(false);
    if (selection.kind === 'vault') lockVault();
    setSelection({ kind: 'notes', folderId: id });
    setSelectedNoteId(null);
  }

  /** Selecting a note pushes the mobile detail view (and dismisses the drawer). */
  function selectNote(id: string): void {
    setSelectedNoteId(id);
    setDrawerOpen(false);
  }

  /**
   * Locks the vault session: the server drops the DEK and the browser drops
   * every decrypted body it was holding (the effect below clears the maps).
   */
  function lockVault(): void {
    void vaultApi.lock().catch(() => undefined);
    setVaultUnlocked(false);
    setSelectedNoteId(null);
  }

  function toggleCollapse(id: string): void {
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function createNote(): void {
    const folderId = selection.kind === 'notes' ? selection.folderId : null;
    const now = nowDate();
    const note: NoteRecord = {
      id: newId(),
      folderId,
      title: 'Untitled',
      content: '',
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
      trashedFrom: null,
      isVaulted: false,
      vaultCiphertextBase64: null,
      vaultedFrom: null,
      version: 1,
    };
    // Mutations write the state ref first: the sync engine reads it
    // synchronously when the push batch is built, and the ref otherwise only
    // catches up on the next render — a "setNotes then sync" order used to
    // push the pre-edit record and clear the dirty flag on it.
    stateRef.current.notes = [note, ...stateRef.current.notes];
    setNotes([...stateRef.current.notes]);
    markDirty(stateRef.current, 'note', note.id);
    // A brand-new note lives in the active list, not trash/vault.
    if (selection.kind !== 'notes') setSelection({ kind: 'notes', folderId });
    setSelectedNoteId(note.id);
    void runSyncFlow();
  }

  function createFolder(): void {
    const now = nowDate();
    const folder: FolderRecord = {
      id: newId(),
      name: 'New Folder',
      parentId: null,
      sortOrder: null,
      createdAt: now,
      updatedAt: now,
      version: 1,
    };
    stateRef.current.folders = [...stateRef.current.folders, folder];
    setFolders([...stateRef.current.folders]);
    markDirty(stateRef.current, 'folder', folder.id);
    void runSyncFlow();
  }

  /**
   * Commits editor changes.
   *
   * For a vaulted note the body is never stored in the clear: it is re-encrypted
   * server-side and only the ciphertext is retained, matching
   * `persistVaultNote` in NoteStore.swift.
   */
  function commitDraft(draft: EditorDraft): void {
    if (!selectedNote) return;
    commitForNote(selectedNote.id, draft);
  }

  /**
   * Persists a draft for a specific note.
   *
   * The path for editor commits and for AI Organize results alike — an
   * organize run finishes asynchronously and must write into its pinned note,
   * never into whatever note happens to be selected by then.
   */
  function commitForNote(noteId: string, draft: EditorDraft): void {
    const note = stateRef.current.notes.find((candidate) => candidate.id === noteId);
    if (!note) return;
    const title = sanitizeTitle(draft.title);

    if (note.isVaulted) {
      if (!vaultUnlocked) return;
      void vaultApi
        .encrypt({ title, content: draft.content, attachments: null })
        .then(({ ciphertextBase64 }) => {
          stateRef.current.notes = stateRef.current.notes.map((candidate) =>
            candidate.id === noteId
              ? {
                  ...candidate,
                  title: '',
                  content: '',
                  vaultCiphertextBase64: ciphertextBase64,
                  updatedAt: nowDate(),
                }
              : candidate,
          );
          setNotes([...stateRef.current.notes]);
          setDecrypted((previous) => new Map(previous).set(noteId, { title, content: draft.content }));
          markDirty(stateRef.current, 'note', noteId);
          void runSyncFlow();
        })
        .catch((error: unknown) => {
          setStatusMessage(
            error instanceof ApiError ? error.message : 'Could not save the vaulted note.',
          );
        });
      return;
    }

    stateRef.current.notes = stateRef.current.notes.map(
      (candidate) =>
        candidate.id === noteId
          ? { ...candidate, title, content: draft.content, updatedAt: nowDate() }
          : candidate,
    );
    setNotes([...stateRef.current.notes]);
    markDirty(stateRef.current, 'note', noteId);
    void runSyncFlow();
  }

  /**
   * Uploads an attachment and inserts a `jnote-att://` reference into the body.
   *
   * Plain notes store bytes as-is. Vaulted notes get an AES-GCM blob under
   * `vault-attachments/<note-id>/<uuid>.vault`, with the original path kept only
   * in the encrypted payload — matching `sealReferencedFiles` on macOS.
   */
  async function uploadAttachment(file: File, insert: (reference: string) => void): Promise<void> {
    if (!selectedNote) return;
    const noteId = selectedNote.id;
    const bytes = new Uint8Array(await file.arrayBuffer());

    try {
      if (selectedNote.isVaulted) {
        if (!vaultUnlocked) return;
        const manifest = await encryptVaultAttachment(noteId, `${newId()}/${file.name}`, bytes);

        // Merge into the existing manifest list, then reseal the payload so the
        // new attachment is discoverable after the vault is locked again.
        const current = decrypted.get(noteId) ?? { title: '', content: '' };
        const existing = selectedNote.vaultCiphertextBase64
          ? await vaultApi.decrypt(selectedNote.vaultCiphertextBase64)
          : { payload: { title: current.title, content: current.content, attachments: [] } };
        const attachments = [...(existing.payload.attachments ?? []), manifest];
        const { ciphertextBase64 } = await vaultApi.encrypt({
          title: current.title,
          content: current.content,
          attachments,
        });

        stateRef.current.notes = stateRef.current.notes.map(
          (note) =>
            note.id === noteId
              ? { ...note, vaultCiphertextBase64: ciphertextBase64, updatedAt: nowDate() }
              : note,
        );
        setNotes([...stateRef.current.notes]);
        const parsed = parseRelativePath(manifest.originalRelativePath);
        if (parsed) insert(attachmentURL(parsed.id, parsed.filename));
      } else {
        const attachmentId = newId();
        await storageApi.put(
          attachmentKey(attachmentId, file.name),
          bytes,
          file.type || 'application/octet-stream',
        );
        // Hash before the state updater: it must not run inside the callback.
        const hash = await sha256Hex(bytes);
        stateRef.current.pendingAttachments.push({
          id: attachmentId,
          filename: file.name,
          hash,
          size: bytes.byteLength,
          uploadedAt: nowDate(),
        });
        insert(attachmentURL(attachmentId, file.name));
      }
      markDirty(stateRef.current, 'note', noteId);
      await runSyncFlow();
    } catch (error) {
      setStatusMessage(error instanceof ApiError ? error.message : `Could not upload ${file.name}.`);
    }
  }

  /** Moves a note to Trash — recoverable, and synced as a soft delete. */
  function trashSelected(): void {
    if (!selectedNote) return;
    const folderNames = new Map(folders.map((folder) => [folder.id, folder.name]));
    const id = selectedNote.id;
    stateRef.current.notes = stateRef.current.notes.map((note) =>
      note.id === id ? trashNote(note, folderNames) : note,
    );
    setNotes([...stateRef.current.notes]);
    markDirty(stateRef.current, 'note', id);
    setSelectedNoteId(null);
    void runSyncFlow();
  }

  /** Permanent delete: removes locally and queues a hard delete for the push. */
  function deleteSelectedForever(): void {
    if (!selectedNote) return;
    queueDelete(stateRef.current, 'note', selectedNote.id);
    setNotes([...stateRef.current.notes]);
    setSelectedNoteId(null);
    void runSyncFlow();
  }

  function restoreSelected(): void {
    if (!selectedNote) return;
    const id = selectedNote.id;
    stateRef.current.notes = stateRef.current.notes.map((note) =>
      note.id === id ? restoreNote(note) : note,
    );
    setNotes([...stateRef.current.notes]);
    markDirty(stateRef.current, 'note', id);
    void runSyncFlow();
  }

  function removeSelectedFolder(): void {
    if (!selection.folderId) return;
    const id = selection.folderId;
    const { notes: updated } = deleteFolder({ folders, notes }, id);
    stateRef.current.notes = updated;
    stateRef.current.folders = stateRef.current.folders.filter((folder) => folder.id !== id);
    setNotes([...stateRef.current.notes]);
    setFolders([...stateRef.current.folders]);
    for (const note of updated) {
      if (note.deletedAt) markDirty(stateRef.current, 'note', note.id);
    }    queueDelete(stateRef.current, 'folder', id);
    setSelection({ kind: 'notes', folderId: null });
    setSelectedNoteId(null);
    void runSyncFlow();
  }

  /** Resolves one conflict through the dialog and refreshes local state. */
  const resolveConflict = useCallback(
    async (conflict: SyncConflict, choice: ResolveChoice) => {
      try {
        await resolveConflictV2(stateRef.current, conflict, choice);
        setFolders([...stateRef.current.folders]);
        setNotes([...stateRef.current.notes]);
        const remaining = [...stateRef.current.conflicts.values()];
        setConflicts(remaining);
        if (remaining.length === 0) setShowConflicts(false);
        persist();
        await runSyncFlow();
      } catch (error) {
        setStatusMessage(
          error instanceof ApiError ? error.message : 'Could not resolve the conflict.',
        );
      }
    },
    [persist, runSyncFlow],
  );

  const syncLabel = lastSyncAt
    ? `Last sync ${lastSyncAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`
    : null;

  /** Footer status: the live sync phase while running, else the last-sync time. */
  const sidebarStatus = syncing ? (phase ? `${phase}…` : 'Syncing…') : syncLabel;

  const viewTitle =
    selection.kind === 'notes'
      ? folders.find((folder) => folder.id === selection.folderId)?.name ?? 'Notes'
      : selection.kind === 'trash'
        ? 'Trash'
        : 'Vault';

  /** Notices rendered under the header of whichever pane owns the list column. */
  const banners = (
    <>
      {storageSettings?.configured === false && (
        <div className="banner banner-info" role="alert">
          <CloudOff size={14} strokeWidth={1.75} />
          <span>No cloud storage configured yet, so nothing is synced.</span>
          <button
            type="button"
            className="banner-action"
            onClick={() => setShowSettings(true)}
          >
            Configure storage
          </button>
        </div>
      )}

      {conflicts.length > 0 && (
        <div className="banner banner-warning" role="alert">
          <GitMerge size={14} strokeWidth={1.75} />
          <span>
            {conflicts.length === 1
              ? '1 note has a sync conflict — your edits are safe and need resolving.'
              : `${conflicts.length} notes have sync conflicts — your edits are safe and need resolving.`}
          </span>
          <button
            type="button"
            className="banner-action"
            onClick={() => setShowConflicts(true)}
          >
            Resolve
          </button>
        </div>
      )}

      {statusMessage && (
        <div className="banner banner-error" role="alert">
          <AlertTriangle size={14} strokeWidth={1.75} />
          {statusMessage}
        </div>
      )}
    </>
  );

  function signOut(): void {
    // The snapshot is a local copy of the library until it is dropped; the
    // next session must start clean.
    clearLibraryState();
    void logout();
  }

  /** 'detail' while a note is open — drives the mobile view stack. */
  const mobileView = selectedNote ? 'detail' : 'list';

  return (
    <div
      className={`library${drawerOpen ? ' drawer-open' : ''}`}
      data-mobile-view={mobileView}
      ref={libraryRef}
      style={
        {
          '--sidebar-width': `${sidebarWidth}px`,
          '--note-list-width': `${noteListWidth}px`,
        } as React.CSSProperties
      }
    >
      {isMobile && (
        <div className="drawer-scrim" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
      )}

      <Sidebar
        tree={tree}
        counts={counts}
        vaultUnlocked={vaultUnlocked}
        selection={selection}
        collapsed={collapsed}
        onToggleCollapse={toggleCollapse}
        onSelectKind={selectKind}
        onSelectFolder={selectFolder}
        onNewFolder={createFolder}
        onSync={() => void runSyncFlow()}
        onOpenSettings={() => setShowSettings(true)}
        onSignOut={signOut}
        onClose={isMobile ? () => setDrawerOpen(false) : undefined}
        syncing={syncing}
        statusLabel={sidebarStatus}
      />

      <SplitHandle
        variant="sidebar"
        label="Resize sidebar"
        width={sidebarWidth}
        min={metrics.sidebarWidthMin}
        max={sidebarWidthMaxFor(viewportWidth)}
        onResize={setSidebarWidth}
        onCommit={saveSidebarWidth}
      />

      {selection.kind === 'vault' && !vaultUnlocked ? (
        <main className="gate-pane">
          {banners}
          <VaultGate
            onUnlocked={() => {
              setVaultUnlocked(true);
              setStatusMessage(null);
            }}
          />
        </main>
      ) : (
        <>
          <NoteList
            notes={visibleNotes}
            headerTitle={viewTitle}
            selectedId={selectedNoteId}
            onSelect={selectNote}
            onCreate={selection.kind === 'vault' ? undefined : createNote}
            onOpenMenu={isMobile ? () => setDrawerOpen(true) : undefined}
            actions={
              <>
                {selection.kind === 'vault' && vaultUnlocked && (
                  <button
                    type="button"
                    className="button button-quiet button-small"
                    onClick={lockVault}
                  >
                    Lock
                  </button>
                )}
                {selection.kind === 'notes' && selection.folderId && (
                  <button
                    type="button"
                    className="icon-button"
                    onClick={removeSelectedFolder}
                    title="Delete folder"
                    aria-label="Delete folder"
                  >
                    <Trash2 size={14} strokeWidth={1.75} />
                  </button>
                )}
              </>
            }
            banner={banners}
            emptyLabel={
              selection.kind === 'trash'
                ? 'Trash is empty.'
                : selection.kind === 'vault'
                  ? 'No vaulted notes.'
                  : 'No notes yet.'
            }
          />

          <SplitHandle
            variant="content"
            label="Resize note list"
            width={noteListWidth}
            min={columns.noteListWidthMin}
            max={noteListWidthMaxFor(viewportWidth, sidebarWidth)}
            onResize={setNoteListWidth}
            onCommit={saveNoteListWidth}
          />

          <EditorPane
            note={displayedNote}
            vaultedNoteIds={vaultedNoteIds}
            onChange={commitDraft}
            onUpload={uploadAttachment}
            onBack={isMobile ? () => setSelectedNoteId(null) : undefined}
            onOpenHistory={
              selectedNote && !selectedNote.isVaulted ? () => setHistoryNoteId(selectedNote.id) : undefined
            }
            attachmentURLs={selectedVaultAttachmentURLs}
            onDelete={selection.kind === 'trash' ? deleteSelectedForever : trashSelected}
            onRestore={restoreSelected}
            isTrashed={selection.kind === 'trash'}
            organize={organizeView}
            readOnly={selectedNote?.isVaulted === true && !vaultUnlocked}
            readOnlyNotice={
              selectedNote?.isVaulted === true && !vaultUnlocked ? (
                <div className="banner banner-info">
                  <span>This note is locked. Unlock it from Vault in the sidebar.</span>
                </div>
              ) : null
            }
          />
        </>
      )}

      {showConflicts && conflicts.length > 0 && (
        <ConflictDialog
          conflicts={conflicts}
          localNote={(id) => stateRef.current.notes.find((candidate) => candidate.id === id) ?? null}
          onResolve={resolveConflict}
          onClose={() => setShowConflicts(false)}
        />
      )}

      {historyNoteId && (
        <VersionHistoryDialog
          noteId={historyNoteId}
          onRestored={async () => {
            await runSyncFlow();
          }}
          onClose={() => setHistoryNoteId(null)}
        />
      )}

      {showSettings && (
        <StorageSettingsDialog
          initial={storageSettings}
          llm={llmSettings}
          onSaved={() => {
            // Credentials are live on the server already, so re-read the view
            // (it flips the "not configured" banner off) and pull the library.
            void loadStorageSettings();
            void loadLLMSettings();
            void runSyncFlow();
          }}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
}
